/**
 * feat-298 — every new on-screen string on `/quiz-review`, in one place.
 *
 * Same reason as `QUIZ_REVIEW_LANGUAGE_COPY`: a literal-text grep for copy that
 * is visibly on screen returns a true zero when the copy is inlined in JSX.
 * Tests assert against these constants, not against retyped strings.
 *
 * Every HIERARCHY word (Track, Lesson, Drill, Question, …) is built from
 * `QUIZ_TERMS` / `termCount` — never typed here (spec §2). "item(s)" is not a
 * hierarchy level, so it is page copy with its own plural helper (design Q3).
 */
import { QUIZ_TERMS, QuizTermKey, termCount } from '../quizTerminology';

const T = QUIZ_TERMS;

/** Join non-empty parts with the page's existing ` · ` separator (U+00B7). */
export const joinSummary = (parts: string[]): string => parts.filter(Boolean).join(' · ');

/** A count with the level word lower-cased, for a level word inside a sentence. */
export const lowerTermCount = (n: number, key: QuizTermKey): string =>
  termCount(n, key).toLowerCase();

export const QUIZ_REVIEW_NAV_COPY = {
  pageTitle: 'Learn Module — Review',
  editLink: '← Edit mode',
  /** Subtitle before / after the tenant name. `withTracks` false = flat fallback.
   *  feat-299: no longer "Read-only view of…" — the English text is editable now. */
  subtitleLead: (withTracks: boolean): string => {
    const levels = [
      ...(withTracks ? [T.track.singular] : []),
      T.lesson.singular,
      T.drill.singular,
      T.question.singular,
      T.rehearsal.singular,
      T.video.singular,
    ];
    return `Every ${levels.join(', ')}, and ${T.reading.singular} for`;
  },
  subtitleTail: ', in the same order the mobile app shows them.',

  // --- banners ---------------------------------------------------------------
  trackEyebrow: T.track.singular,
  lessonHeading: (n: number) => `${T.lesson.singular} ${n}:`,
  drillHeading: (n: number) => `${T.drill.singular} ${n} —`,
  inactive: '(inactive)',
  /** The TOC's inactive marker (AT-5). */
  inactiveMarker: 'inactive',
  unassignedTitle: `Not in any ${T.track.singular}`,
  unassignedHelper: `${T.lesson.plural} that aren’t in any ${T.track.singular}.`,
  questionsPerSession: (n: number | null): string =>
    n != null
      ? `${lowerTermCount(n, 'question')} per session`
      : `all ${T.question.plural.toLowerCase()} per session`,

  // --- empty states ------------------------------------------------------------
  emptyCurriculum: `No ${T.lesson.plural} defined for this tenant yet.`,
  emptyTrack: `No ${T.lesson.plural} in this ${T.track.singular} yet.`,
  emptyLesson: `No ${T.drill.plural} in this ${T.lesson.singular} yet.`,
  emptyDrill:
    `No ${T.question.plural}, ${T.rehearsal.plural}, ${T.video.plural}, ` +
    `or ${T.reading.plural} in this ${T.drill.singular} yet.`,

  // --- collapsed summaries (design NOTES §6) -----------------------------------
  items: (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`,
  noItemsYet: 'No items yet',

  // --- stub rows (spec §1, NOTES §7) -------------------------------------------
  shownUnder: 'Shown under',
  goToLesson: `Go to ${T.lesson.singular}`,

  // --- toolbar and TOC -----------------------------------------------------------
  sectionsGroupLabel: 'Sections',
  expandAll: 'Expand all',
  collapseAll: 'Collapse all',
  tocLabel: 'Curriculum contents',
  tocHeading: 'Contents',
  hideContents: 'Hide contents',
  contentsButton: 'Contents',
  closeContents: 'Close contents',
  expand: (title: string) => `Expand ${title}`,
  collapse: (title: string) => `Collapse ${title}`,

  // --- card placeholders (wording unchanged; level word now from QUIZ_TERMS) -----
  unnamedRehearsal: `(unnamed ${T.rehearsal.singular.toLowerCase()})`,
  untitledVideo: `(untitled ${T.video.singular.toLowerCase()})`,
  untitledReading: `(untitled ${T.reading.singular.toLowerCase()})`,

  // --- reading card (spec §5) ------------------------------------------------------
  showMore: 'Show more',
  showLess: 'Show less',
} as const;
